import { HttpClient } from '@adapters/HttpClient';
import { CountryModel } from '../models/Country';
import { CourierModel, ServiceModel } from '../models/Courier';
import {
    CoverageRulesResponse,
    CreateCoverageRulePayload,
    ReorderPayload,
    UpdateCoverageRulePayload,
} from '../models/CoverageRule';
import { PaymentMethodModel } from '../models/PaymentMethod';

export interface CouriersResponse {
    data: CourierModel[];
}

export interface ServicesResponse {
    data: ServiceModel[];
}

export class CoverageZonesApiAdapter {
    constructor(private httpClient: HttpClient) { }

    async getCouriers(): Promise<CourierModel[]> {
        const response = await this.httpClient.get<CouriersResponse>('/couriers');

        if (response.data && Array.isArray(response.data)) {
            return response.data;
        } else if (Array.isArray(response)) {
            return response as unknown as CourierModel[];
        }

        return [];
    }

    async getCourierServices(courierId: string): Promise<ServiceModel[]> {
        const response = await this.httpClient.get<ServicesResponse>(
            `/couriers/${courierId}/services`,
            {},
            {},
            false // No mostrar loading global para servicios individuales
        );

        if (response.data && Array.isArray(response.data)) {
            return response.data;
        } else if (Array.isArray(response)) {
            return response as unknown as ServiceModel[];
        }

        return [];
    }

    // ========== MÉTODOS CRUD PARA REGLAS DE COBERTURA ==========

    async getCoverageRules(courierServiceId: number): Promise<CoverageRulesResponse> {
        const response = await this.httpClient.get<CoverageRulesResponse>(
            `/couriers/integrations/${courierServiceId}/services/coverage-rules`,
            {},
            {},
            false // No mostrar loading global
        );

        return response;
    }

    async createCoverageRule(
        courierServiceId: number,
        payload: CreateCoverageRulePayload
    ): Promise<void> {
        await this.httpClient.post(
            `/couriers/services/${courierServiceId}/coverage-rules`,
            payload
        );
    }

    async updateCoverageRule(
        ruleId: number,
        payload: UpdateCoverageRulePayload
    ): Promise<void> {
        await this.httpClient.put(
            `/couriers/services/coverage-rules/${ruleId}`,
            payload
        );
    }

    async deleteCoverageRule(ruleId: number): Promise<void> {
        await this.httpClient.delete(`/couriers/services/coverage-rules/${ruleId}`);
    }

    async reorderCoverageRules(
        courierServiceId: number,
        payload: ReorderPayload
    ): Promise<void> {
        await this.httpClient.put(
            `/couriers/services/${courierServiceId}/coverage-rules/reorder`,
            payload
        );
    }

    async bulkImportCoverageRules(
        courierServiceId: number,
        formData: FormData
    ): Promise<void> {
        await this.httpClient.post(
            `/couriers/services/${courierServiceId}/coverage-rules/bulk-import`,
            formData,
            true,
            {
                headers: { 'Content-Type': 'multipart/form-data' },
            }
        );
    }

    async exportCoverageRules(
        countryId: number,
        courierServiceId?: number
    ): Promise<string> {
        const payload: { country_id: number; courier_service_id?: number } = {
            country_id: countryId,
        };

        if (courierServiceId) {
            payload.courier_service_id = courierServiceId;
        }

        const response = await this.httpClient.post<{ url_file?: string; file_url?: string }>(
            '/couriers/coverage-rules/export',
            payload
        );

        // Manejar diferentes formatos de respuesta
        return response.url_file || response.file_url || '';
    }

    // ========== MÉTODOS PARA PAYMENT METHODS ==========

    async getPaymentMethods(): Promise<PaymentMethodModel[]> {
        const response = await this.httpClient.get<PaymentMethodModel[]>(
            '/payment-methods',
            {},
            {},
            false // No mostrar loading global
        );

        if (Array.isArray(response)) {
            return response;
        }

        return [];
    }

    // ========== MÉTODOS PARA COUNTRIES ==========

    async getCountries(): Promise<CountryModel[]> {
        const response = await this.httpClient.get<CountryModel[]>(
            '/countries',
            {},
            {},
            false // No mostrar loading global
        );

        if (Array.isArray(response)) {
            return response;
        }

        return [];
    }
}

